/**
 * ============================================================
 * KEJJA — Shopping page v2
 *  - Sectioned by category (collapsible)
 *  - Tap row → buy modal (quantity, price, supermarket autocomplete)
 *  - On buy: marks bought + adds to pantry + logs price
 *  - Skip-terms manager (water, salt, etc) — settings inline
 *  - Header total cost, items remaining
 * ============================================================
 */

import { useState, useMemo} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Check, X, Trash2, Settings, ShoppingCart, Loader2,
  ChevronDown, ChevronRight, Coins, Sparkles, RotateCcw,
} from 'lucide-react';
import { useMealsContext } from './MealsLayout';
import {
  useShoppingList, useCreateShoppingItem, useMarkPending,
  useDeleteShoppingItem, useClearBought,
} from '@/hooks/useMeals';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ShoppingListItem } from '@/lib/mealsApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// ============================================================
// Section themes — match the meal plan palette so it feels cohesive
// ============================================================
const CATEGORY_THEME: Record<string, { emoji: string; label: string; bg: string; text: string; chip: string }> = {
  produce:  { emoji: '🥬', label: 'Produce',  bg: 'bg-emerald-50 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-300', chip: 'bg-emerald-100 dark:bg-emerald-900/40' },
  dairy:    { emoji: '🥛', label: 'Dairy',    bg: 'bg-sky-50 dark:bg-sky-950/20',         text: 'text-sky-700 dark:text-sky-300',         chip: 'bg-sky-100 dark:bg-sky-900/40' },
  grains:   { emoji: '🌾', label: 'Grains',   bg: 'bg-amber-50 dark:bg-amber-950/20',     text: 'text-amber-700 dark:text-amber-300',     chip: 'bg-amber-100 dark:bg-amber-900/40' },
  protein:  { emoji: '🍗', label: 'Protein',  bg: 'bg-rose-50 dark:bg-rose-950/20',       text: 'text-rose-700 dark:text-rose-300',       chip: 'bg-rose-100 dark:bg-rose-900/40' },
  pantry:   { emoji: '🧂', label: 'Pantry',   bg: 'bg-violet-50 dark:bg-violet-950/20',   text: 'text-violet-700 dark:text-violet-300',   chip: 'bg-violet-100 dark:bg-violet-900/40' },
  other:    { emoji: '🛒', label: 'Other',    bg: 'bg-slate-50 dark:bg-slate-900/40',     text: 'text-slate-700 dark:text-slate-300',     chip: 'bg-slate-100 dark:bg-slate-800' },
};

const CATEGORY_ORDER = ['produce', 'dairy', 'grains', 'protein', 'pantry', 'other'];

export default function ShoppingPage() {
  const { homeId } = useMealsContext();
  const qc = useQueryClient();
  const [showBought, setShowBought] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showSkipMgr, setShowSkipMgr] = useState(false);
  const [buyItem, setBuyItem] = useState<ShoppingListItem | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const { data, isLoading } = useShoppingList(homeId, showBought);
  const removeItem = useDeleteShoppingItem(homeId);
  const markPending = useMarkPending(homeId);
  const clearBought = useClearBought(homeId);

  const items = data?.items ?? [];
  const pending = items.filter(i => i.status === 'pending');
  const bought = items.filter(i => i.status === 'bought');

  // Group pending by category
  const sections = useMemo(() => {
    const grouped: Record<string, ShoppingListItem[]> = {};
    for (const it of pending) {
      const c = (it.category ?? 'other').toLowerCase();
      const key = CATEGORY_THEME[c] ? c : 'other';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(it);
    }
    // Sort categories per CATEGORY_ORDER, then any extras
    const ordered = [...CATEGORY_ORDER, ...Object.keys(grouped).filter(k => !CATEGORY_ORDER.includes(k))];
    return ordered.filter(k => grouped[k]?.length).map(k => ({ key: k, theme: CATEGORY_THEME[k] ?? CATEGORY_THEME.other, items: grouped[k]! }));
  }, [pending]);

  function toggleSection(key: string) {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  if (!homeId) return null;

  return (
    <div className="space-y-5 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="w-6 h-6 text-emerald-600" /> Shopping
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {pending.length} item{pending.length !== 1 ? 's' : ''} to buy
            {bought.length > 0 && ` · ${bought.length} bought`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setShowSkipMgr(true)}>
            <Settings className="w-4 h-4 mr-1" /> Skip list
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowBought(b => !b)}>
            {showBought ? 'Hide bought' : 'Show bought'}
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Sectioned list */}
      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <div className="text-4xl">🛒</div>
            <div className="text-sm font-medium">List is empty</div>
            <div className="text-xs text-muted-foreground">
              Generate a shopping list from your meal plan, or add items manually.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sections.map(sec => {
            const open = !collapsed.has(sec.key);
            return (
              <div key={sec.key} className={cn('rounded-2xl border-2 overflow-hidden', sec.theme.bg)}>
                <button
                  onClick={() => toggleSection(sec.key)}
                  className={cn('w-full px-4 py-3 flex items-center gap-3 hover:bg-white/30 dark:hover:bg-white/5 transition', sec.theme.text)}
                >
                  <span className="text-xl">{sec.theme.emoji}</span>
                  <span className="font-semibold uppercase tracking-wide text-sm">{sec.theme.label}</span>
                  <span className={cn('text-xs px-2 py-0.5 rounded-full', sec.theme.chip)}>
                    {sec.items.length}
                  </span>
                  <span className="ml-auto">
                    {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </span>
                </button>
                {open && (
                  <div className="bg-card/40 backdrop-blur p-2 space-y-1">
                    {sec.items.map(item => (
                      <ItemRow key={item.id}
                               item={item}
                               onBuy={() => setBuyItem(item)}
                               onRemove={() => removeItem.mutate(item.id)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bought section */}
      {showBought && bought.length > 0 && (
        <Card>
          <CardContent className="p-3 space-y-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Bought</span>
              <Button size="sm" variant="ghost" onClick={() => clearBought.mutate()} disabled={clearBought.isPending}>
                <Trash2 className="w-3 h-3 mr-1" /> Clear bought
              </Button>
            </div>
            {bought.map(item => (
              <div key={item.id} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm opacity-70">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="line-through flex-1">{item.name}</span>
                {item.quantity && <span className="text-xs text-muted-foreground">{item.quantity}</span>}
                <button onClick={() => markPending.mutate(item.id)}
                        className="text-muted-foreground hover:text-foreground" title="Move back to list">
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Add modal */}
      {showAdd && (
        <AddItemModal homeId={homeId} onClose={() => setShowAdd(false)}
                      onCreated={() => { qc.invalidateQueries({ queryKey: ['shopping', homeId] }); setShowAdd(false); }} />
      )}

      {/* Buy modal */}
      {buyItem && (
        <BuyItemModal homeId={homeId} item={buyItem}
                      onClose={() => setBuyItem(null)}
                      onBought={() => {
                        qc.invalidateQueries({ queryKey: ['shopping', homeId] });
                        qc.invalidateQueries({ queryKey: ['pantry', homeId] });
                        setBuyItem(null);
                      }} />
      )}

      {/* Skip terms manager */}
      {showSkipMgr && <SkipTermsModal homeId={homeId} onClose={() => setShowSkipMgr(false)} />}
    </div>
  );
}

// ============================================================
// Single row
// ============================================================
function ItemRow({ item, onBuy, onRemove }: {
  item: ShoppingListItem; onBuy: () => void; onRemove: () => void;
}) {
  return (
    <div className="group flex items-center gap-2 bg-card rounded-lg px-3 py-2 hover:shadow-sm transition">
      <button onClick={onBuy}
              className="w-8 h-8 rounded-full border-2 border-emerald-500/40 hover:bg-emerald-500 hover:border-emerald-500 hover:text-white flex items-center justify-center transition flex-shrink-0">
        <Check className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.name}</div>
        {item.quantity && <div className="text-[11px] text-muted-foreground">{item.quantity}</div>}
      </div>
      <button onClick={onRemove}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================
// Buy modal — quantity / price / supermarket
// ============================================================
function BuyItemModal({ homeId, item, onClose, onBought }: {
  homeId: string; item: ShoppingListItem; onClose: () => void; onBought: () => void;
}) {
  const [quantity, setQuantity] = useState(item.quantity ?? '');
  const [price, setPrice] = useState('');
  const [supermarket, setSupermarket] = useState('');
  const supermarketsQ = useQuery({
    queryKey: ['supermarkets', homeId],
    queryFn: () => api.get<{ supermarkets: string[] }>(`/homes/${homeId}/shopping-pantry/supermarkets`),
  });
  const lastPricesQ = useQuery({
    queryKey: ['last-prices', homeId],
    queryFn: () => api.get<{ lastPrices: Array<{ item_name: string; price_minor: number; supermarket: string | null }> }>(`/homes/${homeId}/shopping-pantry/last-prices`),
  });
  const lastPrice = useMemo(() => {
    const lp = lastPricesQ.data?.lastPrices?.find(p => p.item_name.toLowerCase() === item.name.toLowerCase());
    return lp;
  }, [lastPricesQ.data, item.name]);

  const buyMut = useMutation({
    mutationFn: async () => {
      // 1. Update quantity if changed
      if (quantity && quantity !== item.quantity) {
        await api.patch(`/homes/${homeId}/meals/shopping/${item.id}`, { quantity });
      }
      // 2. Mark bought (existing endpoint)
      await api.post(`/homes/${homeId}/meals/shopping/${item.id}/buy`, {});
      // 3. Move to pantry + log price
      const priceMinor = price ? Math.round(Number(price) * 100) : null;
      await api.post(`/homes/${homeId}/shopping-pantry/items/${item.id}/move-to-pantry`, {
        priceMinor,
        supermarket: supermarket || null,
      });
    },
    onSuccess: () => {
      toast.success(`${item.name} added to pantry${price ? ` · KES ${price}` : ''}`);
      onBought();
    },
    onError: (e: any) => toast.error(e?.message ?? 'Could not complete purchase'),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            <span className="font-semibold text-sm">Mark as bought</span>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Item</div>
            <div className="font-medium">{item.name}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Quantity</div>
            <Input placeholder="e.g. 2 kg" value={quantity} onChange={e => setQuantity(e.target.value)} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1 flex items-center gap-2">
              Price (KES)
              {lastPrice && (
                <button onClick={() => setPrice(String(lastPrice.price_minor / 100))}
                        className="text-[10px] text-emerald-600 hover:underline">
                  Last: KES {(lastPrice.price_minor / 100).toLocaleString()}
                </button>
              )}
            </div>
            <div className="relative">
              <Coins className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" type="number" inputMode="decimal"
                     placeholder="optional"
                     value={price}
                     onChange={e => setPrice(e.target.value)} />
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-1">Supermarket</div>
            <Input placeholder="e.g. Naivas, Quickmart…" value={supermarket}
                   onChange={e => setSupermarket(e.target.value)}
                   list="supermarkets-datalist" />
            <datalist id="supermarkets-datalist">
              {(supermarketsQ.data?.supermarkets ?? []).map(s => <option key={s} value={s} />)}
              {/* Common Kenyan supermarkets as defaults */}
              {['Naivas', 'Quickmart', 'Carrefour', 'Chandarana', 'Cleanshelf', 'Eastmatt'].map(s => (
                <option key={`d-${s}`} value={s} />
              ))}
            </datalist>
          </div>
          <Button onClick={() => buyMut.mutate()} disabled={buyMut.isPending}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            {buyMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Confirm purchase
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Add item modal — manual add
// ============================================================
function AddItemModal({ homeId, onClose, onCreated }: { homeId: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [category, setCategory] = useState('other');
  const create = useCreateShoppingItem(homeId);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!name) return;
    create.mutate({ name, quantity: quantity || null, category, status: 'pending' } as any, {
      onSuccess: () => { toast.success('Added'); onCreated(); },
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={handleAdd} className="bg-card rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add to list</span>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <Input placeholder="Item name" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <Input placeholder="Quantity (e.g. 1 kg)" value={quantity} onChange={e => setQuantity(e.target.value)} />
          <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border bg-background text-sm">
            {CATEGORY_ORDER.map(c => (
              <option key={c} value={c}>{CATEGORY_THEME[c]?.emoji} {CATEGORY_THEME[c]?.label}</option>
            ))}
          </select>
          <Button type="submit" disabled={!name || create.isPending} className="w-full">
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Skip terms manager
// ============================================================
function SkipTermsModal({ homeId, onClose }: { homeId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [newTerm, setNewTerm] = useState('');
  const termsQ = useQuery({
    queryKey: ['skip-terms', homeId],
    queryFn: () => api.get<{ terms: Array<{ id: string; term: string }> }>(`/homes/${homeId}/shopping-pantry/skip-terms`),
  });
  const addMut = useMutation({
    mutationFn: (term: string) => api.post(`/homes/${homeId}/shopping-pantry/skip-terms`, { term }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['skip-terms', homeId] }); setNewTerm(''); },
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => api.delete(`/homes/${homeId}/shopping-pantry/skip-terms/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['skip-terms', homeId] }),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <span className="font-semibold text-sm flex items-center gap-2"><Settings className="w-4 h-4" /> Skip list</span>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-xs text-muted-foreground">
            Items containing these words will be skipped when generating a shopping list.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (newTerm) addMut.mutate(newTerm); }}
                className="flex gap-2">
            <Input placeholder="e.g. water, salt" value={newTerm} onChange={e => setNewTerm(e.target.value)} />
            <Button type="submit" size="sm" disabled={!newTerm || addMut.isPending}>Add</Button>
          </form>
          <div className="space-y-1">
            {(termsQ.data?.terms ?? []).map(t => (
              <div key={t.id} className="flex items-center gap-2 bg-muted/40 rounded px-3 py-1.5">
                <span className="flex-1 text-sm">{t.term}</span>
                <button onClick={() => removeMut.mutate(t.id)} className="text-muted-foreground hover:text-destructive">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(termsQ.data?.terms ?? []).length === 0 && (
              <div className="py-4 text-center text-xs text-muted-foreground">No skip terms set.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
